/**
 * SSC header navigation v3 (snippets/mega-panel.liquid, assets/mega-panel.css).
 *
 * Every [data-mega-trigger] in the nav owns one [data-mega-content] block inside the shared panel
 * (SHOP columns + promo + bar, CLUB columns, NEWSLETTER form). Switching between triggers while the
 * panel is open swaps the content in place, without closing and reopening.
 * - mouse: opens after ~150ms hover intent; closes ~200ms after the pointer leaves the nav and panel
 * - click: buttons (CLUB, NEWSLETTER) toggle; a link trigger (SHOP) follows its link with the mouse
 * - keyboard: Enter/Space on a button toggles; Space or ArrowDown on a link trigger opens;
 *   Tab from an open trigger moves into its panel, Tab past the panel's last control moves on to the
 *   next nav item; Esc closes and returns focus to the trigger without reopening
 * - touch: the first tap opens, a second tap on a link trigger follows the link
 * Closes on outside click, when focus leaves the nav, and below the desktop breakpoint.
 * While open, the active trigger has aria-expanded="true": that also switches the header from the
 * blended state over the hero to the white header (blocks/_header-logo.liquid), and
 * window.SSCHeader.setPanelOpen() shows the logo meanwhile (assets/ssc-header-logo.js).
 *
 * Also: the drawer's Search entry ([data-ssc-drawer-search], opens #search-modal through its
 * on:click) closes the menu drawer first.
 */
(function () {
  'use strict';

  var OPEN_DELAY = 150;
  var SWITCH_DELAY = 80;
  var CLOSE_DELAY = 200;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var desktop = window.matchMedia('(min-width: 750px)');

  function setup(nav) {
    if (nav.__sscMegaPanel) return;
    var panel = nav.querySelector('[data-mega-panel]');
    var triggers = Array.prototype.slice.call(nav.querySelectorAll('[data-mega-trigger]'));
    if (!panel || !triggers.length) return;
    nav.__sscMegaPanel = true;

    var openKey = null;
    var openedByHover = false;
    var openTimer = 0;
    var closeTimer = 0;
    var lastTrigger = null;
    var lastPointerType = 'mouse';
    var suppressOpen = false;

    function contentFor(key) {
      return panel.querySelector('[data-mega-content="' + key + '"]');
    }

    function focusables(container) {
      return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
        return el.getClientRects().length > 0;
      });
    }

    function clearTimers() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    }

    /** @param {string|null} key - content to show, null closes */
    function setOpen(key) {
      clearTimers();
      if (key && (!desktop.matches || suppressOpen)) key = null;
      if (key === openKey) return;
      var wasOpen = openKey !== null;
      openKey = key;

      // Keep the last content visible while the panel fades out
      if (key) {
        Array.prototype.forEach.call(panel.querySelectorAll('[data-mega-content]'), function (content) {
          content.hidden = content.getAttribute('data-mega-content') !== key;
        });
      } else {
        openedByHover = false;
      }

      nav.classList.toggle('is-open', !!key);
      panel.inert = !key;
      triggers.forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', trigger.getAttribute('data-mega-trigger') === key ? 'true' : 'false');
      });
      // Header shows the logo while a panel is open (assets/ssc-header-logo.js)
      if (wasOpen !== !!key && window.SSCHeader) window.SSCHeader.setPanelOpen(!!key);
    }

    function scheduleOpen(key) {
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      if (openKey === key) return;
      openTimer = setTimeout(
        function () {
          setOpen(key);
          openedByHover = true;
        },
        openKey ? SWITCH_DELAY : OPEN_DELAY
      );
    }

    function scheduleClose() {
      clearTimeout(openTimer);
      if (!openKey) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        setOpen(null);
      }, CLOSE_DELAY);
    }

    function close(returnFocus) {
      if (!openKey) return;
      setOpen(null);
      if (returnFocus && lastTrigger) {
        // Returning focus must not reopen the panel
        suppressOpen = true;
        lastTrigger.focus();
        suppressOpen = false;
      }
    }

    /** The next top-level nav control after a trigger, if any */
    function nextNavControl(trigger) {
      var item = trigger.closest('li');
      var next = item && item.nextElementSibling;
      while (next) {
        var control = focusables(next)[0];
        if (control) return control;
        next = next.nextElementSibling;
      }
      return null;
    }

    triggers.forEach(function (trigger) {
      var key = trigger.getAttribute('data-mega-trigger');
      var isLink = trigger.tagName === 'A';

      trigger.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'mouse') return;
        lastTrigger = trigger;
        scheduleOpen(key);
      });

      trigger.addEventListener('pointerleave', function (event) {
        // Leaving before the intent delay cancels a pending open
        if (event.pointerType === 'mouse' && openKey !== key) clearTimeout(openTimer);
      });

      trigger.addEventListener('pointerdown', function (event) {
        lastPointerType = event.pointerType;
      });

      trigger.addEventListener('click', function (event) {
        var pointerType = lastPointerType;
        lastPointerType = 'mouse';
        lastTrigger = trigger;
        var keyboard = event.detail === 0;

        if (isLink) {
          // Touch: open first, follow the link on the second tap. Mouse and Enter follow the link.
          if (pointerType !== 'mouse' && !keyboard && openKey !== key) {
            event.preventDefault();
            setOpen(key);
          }
          return;
        }

        event.preventDefault();
        if (openKey === key && openedByHover && !keyboard) {
          // Hover already opened it: the click keeps it open instead of toggling it shut
          openedByHover = false;
          clearTimers();
          return;
        }
        setOpen(openKey === key ? null : key);
      });

      trigger.addEventListener('keydown', function (event) {
        var content = contentFor(key);
        if (isLink && (event.key === ' ' || event.key === 'Spacebar')) {
          event.preventDefault();
          lastTrigger = trigger;
          setOpen(openKey === key ? null : key);
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          lastTrigger = trigger;
          setOpen(key);
          var first = content && focusables(content)[0];
          if (first) first.focus();
        } else if (event.key === 'Tab' && !event.shiftKey && openKey === key && content) {
          // The panel sits after the nav list in the DOM: Tab goes into the open panel first
          var firstInPanel = focusables(content)[0];
          if (firstInPanel) {
            event.preventDefault();
            firstInPanel.focus();
          }
        }
      });
    });

    panel.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || !openKey || !lastTrigger) return;
      var content = contentFor(openKey);
      var items = content ? focusables(content) : [];
      if (!items.length) return;
      if (event.shiftKey && document.activeElement === items[0]) {
        event.preventDefault();
        lastTrigger.focus();
      } else if (!event.shiftKey && document.activeElement === items[items.length - 1]) {
        var next = nextNavControl(lastTrigger);
        if (next) {
          event.preventDefault();
          setOpen(null);
          next.focus();
        }
      }
    });

    // Hovering anywhere in the nav (items or panel) keeps it open; leaving starts the close delay
    nav.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse') clearTimeout(closeTimer);
    });
    nav.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'mouse') scheduleClose();
    });

    nav.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && openKey) {
        event.stopPropagation();
        close(true);
      }
    });

    // relatedTarget is null for clicks on non-focusable panel areas; outside clicks are handled below
    nav.addEventListener('focusout', function (event) {
      if (openKey && event.relatedTarget && !nav.contains(event.relatedTarget)) close(false);
    });

    function onDocumentPointerDown(event) {
      if (!nav.isConnected) {
        teardown();
        return;
      }
      if (openKey && !nav.contains(event.target)) close(false);
    }

    function onBreakpointChange() {
      if (!desktop.matches) close(false);
    }

    function teardown() {
      clearTimers();
      if (openKey && window.SSCHeader) window.SSCHeader.setPanelOpen(false);
      document.removeEventListener('pointerdown', onDocumentPointerDown);
      desktop.removeEventListener('change', onBreakpointChange);
    }

    document.addEventListener('pointerdown', onDocumentPointerDown);
    desktop.addEventListener('change', onBreakpointChange);
  }

  function init() {
    document.querySelectorAll('[data-ssc-mega-panel]').forEach(setup);
  }

  // Drawer Search: close the menu drawer; the button's on:click opens the search modal
  if (!window.__sscDrawerSearch) {
    window.__sscDrawerSearch = true;
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element) || !target.closest('[data-ssc-drawer-search]')) return;
      var drawer = target.closest('header-drawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();
    });
  }

  // Drawer country select: switching the country submits its localization form
  if (!window.__sscDrawerCountry) {
    window.__sscDrawerCountry = true;
    document.addEventListener('change', function (event) {
      var select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.matches('[data-ssc-drawer-country]')) return;
      if (select.form) select.form.submit();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
